import { useEffect, useMemo } from 'react'
import useThemeSettings from '@v4/hooks/useThemeSettings'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import { getPageTheme } from '@v4/utils/theme'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import AppStore from '@v4/store/appStore'
import PageData from '@v4/utils/data/PageData'
import useGeneralMetaData from '@v4/hooks/useGeneralMetaData'
import PreviewSelector from '@v4/utils/selectors/PreviewSelector'
import ThemeSelector from '@v4/utils/selectors/ThemeSelector'

import { DESKTOP_FONT_SIZE_OLD, LINE_HEIGHT } from '@v4/constants/pages'
import { getFontUrl, loadFont } from './Page.utils'
import useStyles from './Page.styled'

export const usePage = ({ children = null }) => {
	const metaData = useGeneralMetaData()
	const themeSettings = useThemeSettings(metaData)
	const scorecardData = ScorecardSelector.useScorecard()
	const themeScorecardData = ThemeSelector.useScorecardData()
	const type = PageBuilderSelector.useType()
	const mode = AppStore.useStoreState(state => state.general.mode)
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const sorting = PreviewSelector.useSorting()
	const directionality = ScorecardSelector.useDirection()
	const classes = useStyles({ sorting })

	const themeTypography = ThemeSelector.useTypography()

	const {
		'typography.h1': typographyH1MetaData,
		'typography.body': typographyBodyMetaData,
		'typography.enabled': typographyEnabledMetaData,
	} = metaData

	const pageTypography = useMemo(() => {
		if (!typographyH1MetaData || !typographyBodyMetaData || !typographyEnabledMetaData?.toBoolean()) return null

		const h1 = typographyH1MetaData.typography
		const body = typographyBodyMetaData.typography
		const scaleTypography = (typography, factor, lineHeight) => {
			return {
				...typography,
				fontSize: Math.round((typography.fontSize || 16) * factor),
				lineHeight: lineHeight || typography.lineHeight,
			}
		}

		return {
			h1,
			h2: scaleTypography(h1, DESKTOP_FONT_SIZE_OLD.h2 / DESKTOP_FONT_SIZE_OLD.h1, LINE_HEIGHT.h2),
			h3: scaleTypography(h1, DESKTOP_FONT_SIZE_OLD.h3 / DESKTOP_FONT_SIZE_OLD.h1, LINE_HEIGHT.h3),
			h4: scaleTypography(h1, DESKTOP_FONT_SIZE_OLD.h4 / DESKTOP_FONT_SIZE_OLD.h1, LINE_HEIGHT.h4),
			h5: scaleTypography(h1, DESKTOP_FONT_SIZE_OLD.h5 / DESKTOP_FONT_SIZE_OLD.h1, LINE_HEIGHT.h5),
			h6: scaleTypography(h1, DESKTOP_FONT_SIZE_OLD.h6 / DESKTOP_FONT_SIZE_OLD.h1, LINE_HEIGHT.h6),
			body,
		}
	}, [typographyBodyMetaData, typographyEnabledMetaData, typographyH1MetaData])

	const typography = typographyEnabledMetaData?.toBoolean() ? pageTypography : themeTypography

	const scorecard = useMemo(() => {
		return _.merge({}, scorecardData, themeScorecardData)
	}, [scorecardData, themeScorecardData])

	const theme = useMemo(() => {
		return getPageTheme({
			settings: themeSettings,
			fallback: scorecard,
			mode,
			isPdf: PageData.isTypeResultPdf(type),
			directionality,
			typography,
		})
	}, [themeSettings, scorecard, mode, type, directionality, typography])

	const { bodyTextFont, headingTextFont } = themeSettings
	const customFonts = useMemo(
		() => ({
			body: {
				name: scorecard.settings.custom_body_font,
				url: scorecard.custom_body_font_url,
			},
			heading: {
				name: scorecard.settings.custom_heading_font,
				url: scorecard.custom_heading_font_url,
			},
		}),
		[
			scorecard.custom_body_font_url,
			scorecard.custom_heading_font_url,
			scorecard.settings.custom_body_font,
			scorecard.settings.custom_heading_font,
		],
	)

	const fontList = useMemo(() => {
		const result = []
		_.forEach(
			typography,
			({ fontFamily }) => {
				if (_.includes([bodyTextFont, headingTextFont], fontFamily)) {
					return
				}
				const customFont =
					(fontFamily === customFonts.body.name && customFonts.body) ||
					(fontFamily === customFonts.heading.name && customFonts.heading) ||
					undefined

				const fontUrl = getFontUrl(fontFamily, customFont)

				if (fontUrl) {
					result.push({
						key: `font-css ${fontFamily}`,
						url: fontUrl,
					})
				}
			},
			[],
		)
		return _.uniqBy(result, 'key')
	}, [bodyTextFont, customFonts.body, customFonts.heading, headingTextFont, typography])

	useEffect(() => {
		if (!isBuilderMode) return
		loadFont(`bodyTextFont-css`, bodyTextFont, customFonts.body)
		loadFont('heading-font-css', headingTextFont, customFonts.heading)
	}, [bodyTextFont, customFonts.body, customFonts.heading, headingTextFont, isBuilderMode, typography])

	return [
		{
			children,
			theme,
			metaData,
			classes,
			fontList,
		},
	]
}
